import React from 'react'
import { Card, CardContent, AsyncContentProgress, FloatingActionButton } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useCommunityEnabled } from '@/modules/comunidade/hooks/useCommunityEnabled'
import { useAnalysisEnabled } from '../hooks/useAnalysisEnabled'
import { useAnalysisEditorEnabled } from '../hooks/useAnalysisEditorEnabled'
import { useAnalysisList } from '../hooks/useAnalysisList'
import { createAnalysis } from '../services/analysisService'
import { AnalysisHeader } from '../components/AnalysisHeader'
import { NewAnalysisModal, type NewAnalysisModalSubmitParams } from '../components/NewAnalysisModal'
import { AnalysisList } from '../components/AnalysisList'
import { useLocation } from 'react-router-dom'
import { uploadAndAddAnalysisPostAttachments } from '../services/analysisPostAttachmentsUploadService'
import { useAnalysisHeaderWarningText } from '../hooks/useAnalysisHeaderWarningText'
import { Pencil } from 'lucide-react'

export default function AnaliseCriptoPage() {
  const enabled = useAnalysisEnabled()
  const communityEnabled = useCommunityEnabled()
  const editorEnabled = useAnalysisEditorEnabled()
  const headerWarningText = useAnalysisHeaderWarningText()
  const { profile } = useAuth()
  const { add: toast } = useToast()
  const [createOpen, setCreateOpen] = React.useState(false)
  const location = useLocation()

  const canManagePosts = profile?.role === 'master' || editorEnabled === true

  const { items, loading, loadingMore, hasMore, error, reload, loadMore } = useAnalysisList(25)

  const focusParams = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '')
      const focusRaw = params.get('focus')
      const msgRaw = params.get('community_message_id')
      const focusId = focusRaw ? Number(String(focusRaw).trim()) : null
      const messageId = msgRaw ? Number(String(msgRaw).trim()) : null
      return {
        focusAnalysisId:
          Number.isFinite(focusId) && (focusId as number) > 0
            ? Math.floor(focusId as number)
            : null,
        focusMessageId:
          Number.isFinite(messageId) && (messageId as number) > 0
            ? Math.floor(messageId as number)
            : null,
      }
    } catch {
      return { focusAnalysisId: null, focusMessageId: null }
    }
  }, [location.search])

  React.useEffect(() => {
    const focusAnalysisId = focusParams.focusAnalysisId
    const focusMessageId = focusParams.focusMessageId
    if (!focusAnalysisId || !focusMessageId) return

    const exists = items.some((it) => Number(it.id) === focusAnalysisId)
    if (exists) return
    if (!hasMore) return
    if (loading || loadingMore) return

    loadMore()
  }, [
    focusParams.focusAnalysisId,
    focusParams.focusMessageId,
    hasMore,
    items,
    loadMore,
    loading,
    loadingMore,
  ])

  if (enabled === null) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Carregando acesso ao modulo de Analises Cripto...
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!enabled) {
    return (
      <div className="mx-auto mt-8 w-full max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-foreground/70">
            Seu plano ou papel atual nao possui acesso ao modulo de Analises Cripto.
          </CardContent>
        </Card>
      </div>
    )
  }

  async function handleCreateAnalysis(params: NewAnalysisModalSubmitParams) {
    try {
      const created = await createAnalysis({
        title: params.title,
        html: params.html,
        linkedOpportunityId: params.linkedOpportunityId,
        linkedTimelineRootId: params.linkedTimelineRootId,
        courtesyEnabled: params.courtesyEnabled,
      })
      if (created) {
        if (params.newAttachments.length > 0) {
          const res = await uploadAndAddAnalysisPostAttachments(
            created.id,
            params.newAttachments,
          )
          if (res.failedCount > 0) {
            toast({
              title: 'Analise criada, mas alguns anexos falharam',
              description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
              variant: 'warning',
              soft: true,
            })
          }
        }
        toast({
          title: 'Analise criada com sucesso',
          description:
            'Sua analise de mercado foi publicada para os membros autorizados.',
          variant: 'success',
          soft: true,
        })
        reload()
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Erro ao criar analise', e)
      toast({
        title: 'Falha ao criar analise',
        description:
          e?.message || 'Ocorreu um erro ao salvar a analise. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <div className="mx-auto mt-8 w-full max-w-5xl">
      <AnalysisHeader warningText={headerWarningText} />

      {canManagePosts && (
        <NewAnalysisModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSubmit={handleCreateAnalysis}
        />
      )}

      {error && (
        <Card className="mt-4 border border-red-500/40 bg-red-500/5">
          <CardContent className="text-sm text-red-400">
            {error}
          </CardContent>
        </Card>
      )}

      <AsyncContentProgress
        loading={loading}
        label="Carregando analises do mercado..."
        className="mt-4"
      >
        <AnalysisList
          items={items}
          communityEnabled={communityEnabled === true}
          canManagePosts={canManagePosts}
          focusAnalysisId={focusParams.focusAnalysisId}
          focusMessageId={focusParams.focusMessageId}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onReload={reload}
        />
      </AsyncContentProgress>

      {canManagePosts && (
        <FloatingActionButton
          label="Adicionar analise"
          onClick={() => setCreateOpen(true)}
          className="bg-lime-500 text-black hover:bg-lime-400 focus-visible:ring-lime-500"
        >
          <Pencil className="h-6 w-6" />
        </FloatingActionButton>
      )}
    </div>
  )
}
